import { supabase } from '../lib/supabase';

/**
 * One customer search for the CRM home box and the Customers directory (CRM release A).
 *
 * Backed by `crm_find_customers` (migration 20261008800000): customer number, booking code / PNR, phone in any
 * format (07…, +44…, spaces, dashes, brackets), e-mail and name (partial, accents and case ignored). Exact number,
 * booking, phone and e-mail matches come first, then names. Approved staff only; the database decides visibility.
 */

export type CustomerSearchSort =
  | 'relevance'
  | 'activity'
  | 'number'
  | 'name'
  | 'owner'
  | 'next_trip'
  | 'open_cases'
  | 'created'
  | 'turnover';

export type SortDirection = 'asc' | 'desc';

export type CustomerMatchReason =
  | 'customer_number'
  | 'booking_code'
  | 'phone'
  | 'email'
  | 'full_name'
  | 'name'
  | 'email_partial'
  | 'phone_partial'
  | 'company';

export interface CustomerSearchRow {
  entityType: 'customer' | 'company';
  id: string;
  number: number | null;
  name: string;
  email: string | null;
  phone: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  nextTripDate: string | null;
  currentTripReturnDate?: string | null;
  openCases: number;
  lastContactAt: string | null;
  turnover: number;
  createdAt: string | null;
  isTest: boolean;
  tags: string[];
  segment: string | null;
  vipTier: string | null;
  company: string | null;
  matchReason: CustomerMatchReason | null;
  matchedValue: string | null;
}

export interface CustomerSearchOwner {
  userId: string;
  name: string;
  customers: number;
}

export interface CustomerSearchParams {
  query?: string | null;
  owner?: string | null;
  hasUpcomingTrip?: boolean | null;
  hasOpenCase?: boolean | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  segment?: string | null;
  vipTier?: string | null;
  includeTest?: boolean;
  /**
   * Archived customers are left out (default true). `crm_find_customers` already filters `customers.archived_at IS NULL`
   * on the server, so this is enforced there; `false` is not supported (no archived rows are ever returned).
   */
  excludeArchived?: boolean;
  sort?: CustomerSearchSort | null;
  sortDir?: SortDirection | null;
  page?: number;
  pageSize?: number;
}

export interface CustomerSearchPage {
  rows: CustomerSearchRow[];
  total: number;
  page: number;
  pageSize: number;
  sort: CustomerSearchSort;
  sortDir: SortDirection;
  testRecordsHidden: number;
  owners: CustomerSearchOwner[];
}

export interface PossibleDuplicateCustomer {
  id: string;
  number: number | null;
  name: string;
  email: string | null;
  phone: string | null;
  ownerName: string | null;
  reasons: Array<'email' | 'phone' | 'name'>;
}

export interface PossibleDuplicatesResult {
  items: PossibleDuplicateCustomer[];
  restrictedCount: number;
}

export const CUSTOMER_MATCH_REASON_LABEL: Record<CustomerMatchReason, string> = {
  customer_number: 'Customer number',
  booking_code: 'Booking code',
  phone: 'Phone',
  email: 'E-mail',
  full_name: 'Name',
  name: 'Name',
  email_partial: 'Part of e-mail',
  phone_partial: 'Part of phone',
  company: 'Company',
};

const SORTS = new Set<CustomerSearchSort>([
  'relevance', 'activity', 'number', 'name', 'owner', 'next_trip', 'open_cases', 'created', 'turnover',
]);
const REASONS = new Set<string>(Object.keys(CUSTOMER_MATCH_REASON_LABEL));
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type RpcInvoke = (name: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string } | null }>;

function rpc(): RpcInvoke {
  return supabase.rpc.bind(supabase) as unknown as RpcInvoke;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
}

function toRow(raw: unknown): CustomerSearchRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  const id = text(item.id);
  if (!id || !UUID.test(id)) return null;
  const entityType = item.entity_type === 'company' ? 'company' : 'customer';
  const reason = typeof item.match_reason === 'string' && REASONS.has(item.match_reason)
    ? (item.match_reason as CustomerMatchReason) : null;
  const currentTripReturnDate = text(item.current_trip_return_date);
  return {
    entityType,
    id,
    number: num(item.number),
    name: text(item.name) ?? (entityType === 'company' ? 'Company' : 'Customer'),
    email: text(item.email),
    phone: text(item.phone),
    ownerUserId: text(item.owner_user_id),
    ownerName: text(item.owner_name),
    nextTripDate: text(item.next_trip_date),
    ...(currentTripReturnDate ? { currentTripReturnDate } : {}),
    openCases: num(item.open_cases) ?? 0,
    lastContactAt: text(item.last_contact_at),
    turnover: num(item.turnover) ?? 0,
    createdAt: text(item.created_at),
    isTest: item.is_test === true,
    tags: Array.isArray(item.tags) ? item.tags.filter((tag): tag is string => typeof tag === 'string') : [],
    segment: text(item.customer_segment),
    vipTier: text(item.vip_tier),
    company: text(item.company),
    matchReason: reason,
    matchedValue: text(item.matched_value),
  };
}

/**
 * True when the text is too short for a name search: the database needs 3 letters, except for a customer number,
 * Chinese, Japanese or Korean text (one or two characters can be a whole name). Phone numbers (4+ digits), e-mail
 * addresses and booking codes are longer than this anyway. Used only to say "type a little more" instead of "no match".
 */
export function needsMoreCharacters(query: string | null | undefined): boolean {
  const trimmed = (query ?? '').normalize('NFKC').trim();
  if (!trimmed) return false;
  if (/^#?\d{1,18}$/.test(trimmed)) return false;
  if (/[\u1100-\u11ff\u3040-\u30ff\u3400-\u9fff\uac00-\ud7af\uf900-\ufaff]/.test(trimmed)) return false;
  return trimmed.normalize('NFKD').replace(/[\s\p{P}\p{S}\p{M}\p{Cf}]/gu, '').length < 3;
}

function errorMessage(error: { message?: string } | null): string {
  const message = error?.message || '';
  if (/staff access required/i.test(message)) return 'Customer search is for approved staff only.';
  if (/does not exist/i.test(message)) return 'Customer search is not available yet (database update pending).';
  return 'Customer search failed. Please try again.';
}

export async function findCustomers(params: CustomerSearchParams = {}): Promise<CustomerSearchPage> {
  const { data, error } = await rpc()('crm_find_customers', {
    p_query: params.query?.trim() || null,
    p_owner: params.owner || null,
    p_has_upcoming_trip: params.hasUpcomingTrip ?? null,
    p_has_open_case: params.hasOpenCase ?? null,
    p_created_from: params.createdFrom || null,
    p_created_to: params.createdTo || null,
    p_segment: params.segment || null,
    p_vip_tier: params.vipTier || null,
    p_include_test: params.includeTest ?? false,
    p_sort: params.sort || null,
    p_sort_dir: params.sortDir || null,
    p_page: params.page ?? 1,
    p_page_size: params.pageSize ?? 25,
  });
  if (error) throw new Error(errorMessage(error));
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Unexpected customer search response.');
  const payload = data as Record<string, unknown>;
  if (payload.contract_version !== 'crm.find-customers.v1' || !Array.isArray(payload.items)) {
    throw new Error('Unexpected customer search response.');
  }
  const sort = typeof payload.sort === 'string' && SORTS.has(payload.sort as CustomerSearchSort)
    ? (payload.sort as CustomerSearchSort) : 'activity';
  const owners = Array.isArray(payload.owners) ? payload.owners : [];
  return {
    rows: payload.items.map(toRow).filter((row): row is CustomerSearchRow => row !== null),
    total: num(payload.total) ?? 0,
    page: num(payload.page) ?? 1,
    pageSize: num(payload.page_size) ?? 25,
    sort,
    sortDir: payload.sort_dir === 'asc' ? 'asc' : 'desc',
    testRecordsHidden: num(payload.test_records_hidden) ?? 0,
    owners: owners
      .map((owner) => owner as Record<string, unknown>)
      .filter((owner) => typeof owner.user_id === 'string' && UUID.test(owner.user_id))
      .map((owner) => ({ userId: owner.user_id as string, name: text(owner.name) ?? 'Staff member', customers: num(owner.customers) ?? 0 })),
  };
}

export async function findPossibleDuplicateCustomers(input: {
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): Promise<PossibleDuplicatesResult> {
  const { data, error } = await rpc()('crm_find_possible_duplicate_customers', {
    p_email: input.email?.trim() || null,
    p_phone: input.phone?.trim() || null,
    p_first_name: input.firstName?.trim() || null,
    p_last_name: input.lastName?.trim() || null,
  });
  if (error) throw new Error(errorMessage(error));
  const payload = (data && typeof data === 'object' && !Array.isArray(data) ? data : {}) as Record<string, unknown>;
  if (payload.contract_version !== 'crm.possible-duplicates.v1' || !Array.isArray(payload.items)) {
    throw new Error('Unexpected duplicate check response.');
  }
  const items: PossibleDuplicateCustomer[] = [];
  for (const raw of payload.items) {
    if (!raw || typeof raw !== 'object') continue;
    const item = raw as Record<string, unknown>;
    const id = text(item.id);
    if (!id || !UUID.test(id)) continue;
    const reasons = Array.isArray(item.reasons)
      ? item.reasons.filter((reason): reason is 'email' | 'phone' | 'name' => reason === 'email' || reason === 'phone' || reason === 'name')
      : [];
    items.push({
      id,
      number: num(item.number),
      name: text(item.name) ?? 'Customer',
      email: text(item.email),
      phone: text(item.phone),
      ownerName: text(item.owner_name),
      reasons,
    });
  }
  return { items, restrictedCount: num(payload.restricted_count) ?? 0 };
}
